<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, nextTick, reactive, ref, toRefs, watch } from "vue";
import Button from "../../../base-components/Button";
import { FormCheck, FormLabel } from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Preview from "../../../base-components/Preview";

import { useMensajesStore } from "../../../services/mensajes";

// import { useTasasIVAStore } from "../../../services/tasas";
import { useSideMenuStore } from "../../../stores/side-menu";
import { userUserStore } from "../../../stores/user";
import { useMonedasStore } from "../../../services/monedas";
import FormInput from "../../../base-components/Form/FormInput.vue";
import TomSelect from "../../../custom-components/TomSelect/TomSelect.vue";
import { Descuento, LogAcciones, TasaIVA } from "@fixeduy/restaurantestypes";

/** Mismos valores que IMPRESION_POR_DEFECTO en RestaurantesTypes (para uso en template). */
const IMPRESION_POR_DEFECTO = {
  NINGUNO: "ninguno",
  TODOS: "todos",
  DISPOSITIVO_ACTUAL: "dispositivoActual"
} as const;
import { useTasasIVAStore } from "../../../services/tasas";
import { useLogAccionesStore } from "../../../services/logAcciones";
import { getCurrentTime } from "../../../utils/fecha";
import { useProductosCategoriasStore } from "../../../services/productosCategorias";
import FormSelect from "../../../base-components/Form/FormSelect.vue";

const sideMenuStore = useSideMenuStore();
const tasasStore = useTasasIVAStore();
const monedasStore = useMonedasStore();
const userStore = userUserStore();
const mensajesStore = useMensajesStore();
const logAccionesStore = useLogAccionesStore();
const mostrarAgregarDescuentos = ref(false);
const categoriasStore = useProductosCategoriasStore();

const emit = defineEmits<{ (e: "cerrar"): void }>();

const cargando = ref<boolean>(false);

// const tasasIVAactivas = computed(() => {
//   console.log(tasasStore.tasaActivas);
//   return tasasStore.tasaActivas;
// });

let desc: Descuento = {
  nombre: "",
  valor: 0,
  activo: false
};

const formData = reactive({
  tasaIVA: userStore.opcionesGenerales.tasaIVADefecto,
  vistaPrecios: userStore.opcionesGenerales.vistaPrecios,
  moneda: userStore.opcionesGenerales.monedaDefecto,
  sector: "",
  sectores: [...userStore.opcionesGenerales.sectores],
  impresionPorDefecto: [...userStore.opcionesGenerales.impresionPorDefecto],
  habilitarDescuentos: userStore.opcionesGenerales.habilitarDescuentos,
  imprimirComandaCompleta: userStore.opcionesGenerales.imprimirComandaCompleta,
  impresionComandaCompleta:
    userStore.opcionesGenerales.impresionComandaCompleta,
  descuento: { nombre: "", valor: 0, activo: true },
  descuentos: [...userStore.opcionesGenerales.descuentos],
  agruparItems: userStore.opcionesGenerales.agruparItems,
  facturaFixed: userStore.opcionesGenerales.facturaFixed,
  fixedPorDefecto: userStore.opcionesGenerales.fixedPorDefecto,
  impresionAnteFallas: userStore.opcionesGenerales.impresionAnteFallas,
  categoriaDefectoManual: userStore.opcionesGenerales.categoriaDefectoManual,
  integracionHandyHabilitado:
    userStore.opcionesGenerales.integracionPOSHandy.habilitado != null
      ? userStore.opcionesGenerales.integracionPOSHandy.habilitado
      : false,
  integracionOCAHabilitado:
    userStore.opcionesGenerales.integracionPOSOCA.habilitado != null
      ? userStore.opcionesGenerales.integracionPOSOCA.habilitado
      : false,
  integracionFiservHabilitado:
    userStore.opcionesGenerales.integracionPOSFiserv.habilitado != null
      ? userStore.opcionesGenerales.integracionPOSFiserv.habilitado
      : false,
  integracionGetnetHabilitado:
    userStore.opcionesGenerales.integracionPOSGetnet.habilitado != null
      ? userStore.opcionesGenerales.integracionPOSGetnet.habilitado
      : false,
  integracionScanntechHabilitado:
    userStore.opcionesGenerales.integracionPOSScanntech.habilitado != null
      ? userStore.opcionesGenerales.integracionPOSScanntech.habilitado
      : false
});

const rules = {
  tasaIVA: {
    required: helpers.withMessage("El campo IVA no puede estar vacío", required)
  },
  categoriaDefectoManual: {
    required: helpers.withMessage("La categoría no puede estar vacía", required)
  },
  vistaPrecios: {
    required: helpers.withMessage(
      "El campo moneda no puede estar vacío",
      required
    )
  },
  moneda: {
    required: helpers.withMessage(
      "El campo moneda no puede estar vacío",
      required
    )
  }
};

const compararArrays = (a: any[], b: any[]) => {
  if (a.length !== b.length) return false;

  const aOrdenado = [...a].sort();
  const bOrdenado = [...b].sort();

  return aOrdenado.every((val, i) => val === bOrdenado[i]);
};

type Item = {
  nombre: string;
  valor: number;
  activo: boolean;
};
const compararArrayObjetos = (a: Item[], b: Item[]) => {
  if (a.length !== b.length) return false;

  return a.every((objA, i) => {
    const objB = b[i];
    if (!objB) return false;

    const keysA = Object.keys(objA) as (keyof Item)[];
    const keysB = Object.keys(objB) as (keyof Item)[];

    if (keysA.length !== keysB.length) return false;

    return keysA.every(key => objA[key] === objB[key]);
  });
};

const validate = useVuelidate(rules, toRefs(formData));
const cambios: string[] = [];
const handlerGuardar = async () => {
  const result = await validate.value.$validate();
  if (result) {
    if (userStore.opcionesGenerales.monedaDefecto != formData.moneda) {
      cambios.push(
        `Se modifica moneda por defecto: ${userStore.opcionesGenerales.monedaDefecto} -> ${formData.moneda}`
      );
    }
    if (userStore.opcionesGenerales.vistaPrecios != formData.vistaPrecios) {
      cambios.push(
        `Se modifica vista precios: ${userStore.opcionesGenerales.vistaPrecios} -> ${formData.vistaPrecios}`
      );
    }
    if (userStore.opcionesGenerales.tasaIVADefecto != formData.tasaIVA) {
      cambios.push(
        `Se modifica tasa IVA por defecto: ${userStore.opcionesGenerales.tasaIVADefecto} -> ${formData.tasaIVA}`
      );
    }
    if (
      userStore.opcionesGenerales.categoriaDefectoManual !=
      formData.categoriaDefectoManual
    ) {
      cambios.push(
        `Se modifica categoria por defecto en detalle manual: ${userStore.opcionesGenerales.categoriaDefectoManual} -> ${formData.categoriaDefectoManual}`
      );
    }
    if (
      !compararArrays(userStore.opcionesGenerales.sectores, formData.sectores)
    ) {
      cambios.push(
        `Se modifican sectores de impresión: ${userStore.opcionesGenerales.sectores} -> ${formData.sectores}`
      );
    }
    if (
      !compararArrays(
        userStore.opcionesGenerales.impresionPorDefecto,
        formData.impresionPorDefecto
      )
    ) {
      cambios.push(
        `Se modifica impresión por defecto: ${userStore.opcionesGenerales.impresionPorDefecto} -> ${formData.impresionPorDefecto}`
      );
    }
    if (
      userStore.opcionesGenerales.habilitarDescuentos !=
      formData.habilitarDescuentos
    ) {
      cambios.push(
        `Se modifica habilitar descuentos: ${userStore.opcionesGenerales.habilitarDescuentos} -> ${formData.habilitarDescuentos}`
      );
    }

    const antes = userStore.opcionesGenerales.descuentos || [];
    const despues = formData.descuentos || [];

    if (!compararArrayObjetos(antes, despues)) {
      const nombresAntes = antes.map(d => d.nombre).join(", ");
      const nombresDespues = despues.map(d => d.nombre).join(", ");

      cambios.push(
        `Se modifican descuentos: ${nombresAntes} -> ${nombresDespues}`
      );
    }
    if (userStore.opcionesGenerales.agruparItems != formData.agruparItems) {
      cambios.push(
        `Se modifica agrupar ítems: ${userStore.opcionesGenerales.agruparItems} -> ${formData.agruparItems}`
      );
    }

    if (userStore.opcionesGenerales.facturaFixed != formData.facturaFixed) {
      cambios.push(
        `Se modifica factura con Fixed: ${userStore.opcionesGenerales.facturaFixed} -> ${formData.facturaFixed}`
      );
    }
    if (
      userStore.opcionesGenerales.fixedPorDefecto != formData.fixedPorDefecto
    ) {
      cambios.push(
        `Se modifica facturar con Fixed por defecto: ${userStore.opcionesGenerales.fixedPorDefecto} -> ${formData.fixedPorDefecto}`
      );
    }

    if (
      userStore.opcionesGenerales.integracionPOSFiserv.habilitado !=
      formData.integracionFiservHabilitado
    ) {
      cambios.push(
        `Se modifica integración con Fiserv: ${userStore.opcionesGenerales.integracionPOSFiserv.habilitado} -> ${formData.integracionFiservHabilitado}`
      );
    }
    if (
      userStore.opcionesGenerales.integracionPOSHandy.habilitado !=
      formData.integracionHandyHabilitado
    ) {
      cambios.push(
        `Se modifica integración con Handy: ${userStore.opcionesGenerales.integracionPOSHandy.habilitado} -> ${formData.integracionHandyHabilitado}`
      );
    }
    if (
      userStore.opcionesGenerales.integracionPOSOCA.habilitado !=
      formData.integracionOCAHabilitado
    ) {
      cambios.push(
        `Se modifica integración con Handy: ${userStore.opcionesGenerales.integracionPOSOCA.habilitado} -> ${formData.integracionOCAHabilitado}`
      );
    }
    if (
      userStore.opcionesGenerales.integracionPOSGetnet.habilitado !=
      formData.integracionGetnetHabilitado
    ) {
      cambios.push(
        `Se modifica integración con Handy: ${userStore.opcionesGenerales.integracionPOSGetnet.habilitado} -> ${formData.integracionGetnetHabilitado}`
      );
    }
    cargando.value = true;

    await userStore.setOpcionesGenerales({
      tasaIVADefecto: formData.tasaIVA,
      monedaDefecto: formData.moneda,
      vistaPrecios: formData.vistaPrecios,
      sectores: formData.sectores,
      impresionPorDefecto: formData.impresionPorDefecto,
      habilitarDescuentos: formData.habilitarDescuentos,
      imprimirComandaCompleta: formData.imprimirComandaCompleta,
      impresionComandaCompleta: formData.impresionComandaCompleta,
      descuentos: formData.descuentos,
      agruparItems: formData.agruparItems,
      facturaFixed: formData.facturaFixed,
      fixedPorDefecto: formData.fixedPorDefecto,
      impresionAnteFallas: formData.impresionAnteFallas,
      categoriaDefectoManual: formData.categoriaDefectoManual,
      integracionPOSHandy: {
        SystemId: userStore.opcionesGenerales.integracionPOSHandy.SystemId,
        habilitado: formData.integracionHandyHabilitado,
        pos: userStore.opcionesGenerales.integracionPOSHandy.pos
      },
      integracionPOSOCA: {
        SystemId: userStore.opcionesGenerales.integracionPOSOCA.SystemId,
        habilitado: formData.integracionOCAHabilitado,
        pos: userStore.opcionesGenerales.integracionPOSOCA.pos
      },
      integracionPOSFiserv: {
        SystemId: userStore.opcionesGenerales.integracionPOSFiserv.SystemId,
        habilitado: formData.integracionFiservHabilitado,
        pos: userStore.opcionesGenerales.integracionPOSFiserv.pos
      },
      integracionPOSGetnet: {
        EmpHASH: userStore.opcionesGenerales.integracionPOSGetnet.EmpHASH,
        EmpCod: userStore.opcionesGenerales.integracionPOSGetnet.EmpCod,
        habilitado: formData.integracionGetnetHabilitado,
        pos: userStore.opcionesGenerales.integracionPOSGetnet.pos
      },
      integracionPOSScanntech: {
        Empresa: userStore.opcionesGenerales.integracionPOSScanntech.Empresa,
        habilitado: formData.integracionScanntechHabilitado,
        pos: userStore.opcionesGenerales.integracionPOSScanntech.pos
      }
    });
    cargando.value = false;

    sideMenuStore.regenerarMenu();
    mensajesStore.mostrarToast(
      "Opciones editadas con éxito",
      "En la aplicación móvil, para ver los cambios tocá: Configuración -> RECARGAR OPCIONES.",
      mensajesStore.mensajeType.exito
    );
    if (cambios.length) {
      const logAcciones: LogAcciones = {
        id: "",
        accion: cambios,
        modulo: "opciones",
        fecha: getCurrentTime(),
        usuario: userStore.userLoggedIn?.uid!,
        mail: userStore.userLoggedIn?.email!
      };

      await logAccionesStore.guardarLog(logAcciones);
    }
    emit("cerrar");
  }
};

const agregarSector = (e: any) => {
  if (formData.sector != "") {
    formData.sectores.push(formData.sector);
    (document.getElementById("sectores") as HTMLInputElement).value = "";
    formData.sector = "";
  } else {
    e.preventDefault();
  }
};

const normalizarNombre = (s: string) => s.trim().replace(/\s+/g, " ");

const agregarDescuento = (e?: any) => {
  const nombre = normalizarNombre(formData.descuento.nombre);
  const valor = Number(formData.descuento.valor ?? 0);

  if (!nombre) {
    e?.preventDefault?.();
    return;
  }
  const existe = (formData.descuentos ?? []).some(
    d => d.nombre.trim().toLowerCase() === nombre.toLowerCase()
  );
  if (existe) {
    mensajesStore.mostrarToast(
      "Ya existe un descuento con ese nombre",
      nombre,
      mensajesStore.mensajeType.error
    );
    e?.preventDefault?.();
    return;
  }

  const valorSeguro = isNaN(valor) ? 0 : Math.max(0, valor);

  formData.descuentos = formData.descuentos ?? [];
  formData.descuentos.push({
    nombre,
    valor: valorSeguro,
    activo: formData.descuento.activo ?? true
  });
  formData.descuento = { nombre: "", valor: 0, activo: true };
};

const eliminarDescuento = (index: number) => {
  formData.descuentos.splice(index, 1);
};

const toggleDescuentoActivo = (index: number) => {
  formData.descuentos[index].activo = !formData.descuentos[index].activo;
};

// Impresión por defecto: ninguno/todos (solo uno, el otro se deshabilita) o TomSelect con dispositivo + sectores
/** "ninguno" | "todos" | null. Si no es null, se oculta el TomSelect. */
const impresionModoRestrictivo = computed(() => {
  const sel = formData.impresionPorDefecto ?? [];
  if (sel.includes(IMPRESION_POR_DEFECTO.NINGUNO))
    return IMPRESION_POR_DEFECTO.NINGUNO;
  if (sel.includes(IMPRESION_POR_DEFECTO.TODOS))
    return IMPRESION_POR_DEFECTO.TODOS;
  return null;
});

/** Valor para el grupo de radios: "ninguno" | "todos" | "custom". Solo uno puede estar seleccionado. */
const impresionModoRadio = computed(
  () => impresionModoRestrictivo.value ?? "custom"
);

/** Valor del TomSelect (solo dispositivo + sectores). Evita que TomSelect sobrescriba al marcar ninguno/todos. */
const impresionOpcionesCustom = ref<string[]>([]);

/** Sincroniza impresionOpcionesCustom desde formData al pasar a modo custom (sin ninguno/todos). */
watch(
  impresionModoRestrictivo,
  modo => {
    if (modo === null) {
      const sel = formData.impresionPorDefecto ?? [];
      const sectores = formData.sectores ?? [];
      impresionOpcionesCustom.value = sel.filter(
        (v: string) =>
          v === IMPRESION_POR_DEFECTO.DISPOSITIVO_ACTUAL || sectores.includes(v)
      );
    }
  },
  { immediate: true }
);

/** Cuando el usuario cambia el TomSelect (modo custom), actualiza formData. */
watch(
  impresionOpcionesCustom,
  val => {
    if (impresionModoRestrictivo.value === null) {
      formData.impresionPorDefecto = [...val];
    }
  },
  { deep: true }
);

/** Al elegir una opción del grupo de radios: actualiza formData y oculta/muestra TomSelect. */
function setImpresionModo(
  val:
    | typeof IMPRESION_POR_DEFECTO.NINGUNO
    | typeof IMPRESION_POR_DEFECTO.TODOS
    | "custom"
) {
  if (val === IMPRESION_POR_DEFECTO.NINGUNO) {
    if (
      formData.impresionPorDefecto?.length !== 1 ||
      formData.impresionPorDefecto[0] !== IMPRESION_POR_DEFECTO.NINGUNO
    ) {
      formData.impresionPorDefecto = [IMPRESION_POR_DEFECTO.NINGUNO];
    }
    return;
  }

  if (val === IMPRESION_POR_DEFECTO.TODOS) {
    if (
      formData.impresionPorDefecto?.length !== 1 ||
      formData.impresionPorDefecto[0] !== IMPRESION_POR_DEFECTO.TODOS
    ) {
      formData.impresionPorDefecto = [IMPRESION_POR_DEFECTO.TODOS];
    }
    return;
  }

  const nuevo = [...impresionOpcionesCustom.value];

  if (JSON.stringify(formData.impresionPorDefecto) !== JSON.stringify(nuevo)) {
    formData.impresionPorDefecto = nuevo;
  }
}

// Normaliza al cargar: si hay mezcla inválida, priorizar restrictivo o quedarse con específicos
watch(
  () => formData.impresionPorDefecto,
  newVal => {
    if (!Array.isArray(newVal)) return;

    const sel = newVal;
    const sectores = formData.sectores ?? [];
    const tieneNinguno = sel.includes(IMPRESION_POR_DEFECTO.NINGUNO);
    const tieneTodos = sel.includes(IMPRESION_POR_DEFECTO.TODOS);

    if (tieneNinguno) {
      if (sel.length !== 1 || sel[0] !== IMPRESION_POR_DEFECTO.NINGUNO) {
        formData.impresionPorDefecto = [IMPRESION_POR_DEFECTO.NINGUNO];
      }
      return;
    }

    if (tieneTodos) {
      const sinSectores = sel.filter((v: string) => !sectores.includes(v));

      const nuevo =
        sinSectores.length !== sel.length
          ? sinSectores
          : [IMPRESION_POR_DEFECTO.TODOS];

      if (JSON.stringify(nuevo) !== JSON.stringify(sel)) {
        formData.impresionPorDefecto = nuevo;
      }
    }
  }
);

const mostrarEditarTasas = ref(false);

const activarTasa = async (tasa: TasaIVA) => {
  await tasasStore.editarTasaIVA({ ...tasa, activo: true });
  cambios.push(`Se activa tasa ${tasa.id}`);
};
const inactivarTasa = async (tasa: TasaIVA) => {
  cambios.push(`Se inactiva tasa ${tasa.id}`);
  await tasasStore.editarTasaIVA({ ...tasa, activo: false });
};
</script>

<template>
  <Preview class="box mt-5">
    <form class="validate-form p-4" @submit.prevent="">
      <div
        class="border-b sm:flex-row border-slate-200/60 dark:border-darkmode-400"
      >
        <div
          class="w-full pb-6 cursor-pointer flex-row flex"
          @click="
            () => {
              emit('cerrar');
            }
          "
        >
          <div class="flex w-full justify-center mt-2">
            <h1 class="self-center text-xl mt-2">Opciones Generales</h1>
          </div>
        </div>
        <!-- Moneda  -->
        <div class="flex mt-5">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Moneda
            </FormLabel>
          </div>
          <div class="flex flex-col">
            <FormCheck
              class="mb-1"
              v-for="moneda in monedasStore.monedasActivas"
            >
              <template v-if="moneda.activo">
                <FormCheck.Input
                  :id="moneda.id"
                  type="radio"
                  name="`horizontal_radio_button_"
                  :value="moneda.id"
                  v-model="formData.moneda"
                  :class="{
                    'border-danger': validate.moneda.$error
                  }"
                />
                <FormCheck.Label>
                  {{ moneda.simbolo }} - {{ moneda.nombre }}
                </FormCheck.Label>
              </template>
            </FormCheck>
          </div>
        </div>
        <!-- Vista precio  -->
        <!-- <div class="flex mt-5">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Mostrar precios
            </FormLabel>
          </div>
          <div class="flex flex-col">
            <FormCheck class="mb-2">
              <FormCheck.Input
                id="sinIVA"
                type="radio"
                name="`horizontal_radio_button_vistaPrecio"
                value="sinIVA"
                v-model="formData.vistaPrecios"
                :class="{
                  'border-danger': validate.vistaPrecios.$error
                }"
              />
              <FormCheck.Label class="ml-3"> Sin IVA </FormCheck.Label>
            </FormCheck>
            <FormCheck class="mb-2">
              <FormCheck.Input
                id="conIVA"
                type="radio"
                name="`horizontal_radio_button_vistaPrecio"
                value="conIVA"
                v-model="formData.vistaPrecios"
                :class="{
                  'border-danger': validate.vistaPrecios.$error
                }"
              />
              <FormCheck.Label class="ml-3"> Con IVA </FormCheck.Label>
            </FormCheck>
          </div>
        </div> -->
        <!-- Tasa iva  -->
        <div class="col-span-12 lg:col-span-6">
          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Tasa IVA
              </FormLabel>
            </div>
            <TomSelect
              id="tasa"
              v-model="formData.tasaIVA"
              :class="{
                'border-danger': validate.tasaIVA.$error
              }"
              placeholder="IVA"
              class="w-full md:w-1/8"
            >
              <option
                v-for="tasa in tasasStore.tasaActivas"
                :key="tasa.id"
                :value="tasa.id"
              >
                {{ tasa.nombre }}
              </option>
            </TomSelect>
          </div>
        </div>
        <!-- Editar tasas -->
        <div class="col-span-12 lg:col-span-6 mt-5 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel class="w-40 sw:w-20">
                Editar estados de tasas
              </FormLabel>
            </div>

            <div class="flex-1">
              <FormCheck class="mt-2">
                <FormCheck.Input
                  id="editarTasas"
                  type="checkbox"
                  v-model="mostrarEditarTasas"
                />
              </FormCheck>

              <div v-if="mostrarEditarTasas" class="space-y-2 mt-4">
                <div
                  v-for="tasa in tasasStore.tasas"
                  :key="tasa.id"
                  class="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2"
                  :class="{ 'opacity-60': !tasa.activo }"
                >
                  <div class="flex items-center gap-3">
                    <span class="text-sm">
                      {{ tasa.nombre }}
                    </span>

                    <span
                      class="text-xs px-2 py-0.5 rounded-full"
                      :class="
                        tasa.activo
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-600'
                      "
                    >
                      {{ tasa.activo ? "Activa" : "Inactiva" }}
                    </span>
                  </div>

                  <Button
                    v-if="tasa.activo"
                    variant="outline-secondary"
                    size="sm"
                    class="px-2"
                    @click="inactivarTasa(tasa)"
                  >
                    Inactivar
                  </Button>

                  <Button
                    v-else
                    variant="outline-secondary"
                    size="sm"
                    class="px-2"
                    @click="activarTasa(tasa)"
                  >
                    Activar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Sector -->
        <div class="col-span-12 lg:col-span-6 mt-3 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Sectores de impresión
              </FormLabel>
            </div>
            <div class="flex-1">
              <TomSelect
                v-model="formData.sectores"
                placeholder="Selecciona opciones"
                multiple
              >
                <option
                  v-for="sector in formData.sectores"
                  :key="sector"
                  :value="sector"
                >
                  {{ sector }}
                </option>
              </TomSelect>
            </div>
          </div>
        </div>
        <!-- Agregar sectores -->
        <div class="flex mt-5">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Agregar sector impresión
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormInput
              type="text"
              id="sectores"
              v-model.trim="formData.sector"
              placeholder="Nombre de sector a agregar..."
              @blur="agregarSector"
              @keypress="
                e => {
                  if (e.key === ',') {
                    e.preventDefault();
                    agregarSector(e);
                  }
                }
              "
            />
            <Button
              class="my-3"
              variant="secondary"
              size="sm"
              @click="agregarSector"
              >Agregar</Button
            >
          </div>
        </div>
        <!-- Impresión por defecto (al crear productos) -->
        <div class="col-span-12 mt-5 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel class="w-40 sw:w-20">
                Impresión por defecto
              </FormLabel>
            </div>
            <div class="flex-1">
              <p class="text-sm text-slate-500 dark:text-slate-400 mb-3">
                Configuración que se carga por defecto al crear un producto en
                la aplicación móvil.
              </p>
              <div class="flex flex-col gap-2">
                <div class="flex flex-col gap-2">
                  <FormCheck class="flex items-center">
                    <FormCheck.Input
                      :id="`impresion-${IMPRESION_POR_DEFECTO.NINGUNO}`"
                      type="radio"
                      name="impresionPorDefectoModo"
                      :value="IMPRESION_POR_DEFECTO.NINGUNO"
                      :checked="
                        impresionModoRadio === IMPRESION_POR_DEFECTO.NINGUNO
                      "
                      @change="setImpresionModo(IMPRESION_POR_DEFECTO.NINGUNO)"
                    />
                    <FormCheck.Label class="ml-2">
                      En ningún dispositivo
                    </FormCheck.Label>
                  </FormCheck>
                  <FormCheck class="flex items-center">
                    <FormCheck.Input
                      :id="`impresion-${IMPRESION_POR_DEFECTO.TODOS}`"
                      type="radio"
                      name="impresionPorDefectoModo"
                      :value="IMPRESION_POR_DEFECTO.TODOS"
                      :checked="
                        impresionModoRadio === IMPRESION_POR_DEFECTO.TODOS
                      "
                      @change="setImpresionModo(IMPRESION_POR_DEFECTO.TODOS)"
                    />
                    <FormCheck.Label class="ml-2">
                      En todos los dispositivos
                    </FormCheck.Label>
                  </FormCheck>
                  <FormCheck class="flex items-center">
                    <FormCheck.Input
                      id="impresion-custom"
                      type="radio"
                      name="impresionPorDefectoModo"
                      value="custom"
                      :checked="impresionModoRadio === 'custom'"
                      @change="setImpresionModo('custom')"
                    />
                    <FormCheck.Label class="ml-2">
                      Seleccionar sectores y/o dispositivo actual
                    </FormCheck.Label>
                  </FormCheck>
                </div>
                <div v-if="impresionModoRadio === 'custom'" class="mt-2">
                  <TomSelect
                    v-model="impresionOpcionesCustom"
                    placeholder="Dispositivo actual y/o sectores..."
                    multiple
                  >
                    <!-- <option :value="IMPRESION_POR_DEFECTO.DISPOSITIVO_ACTUAL">
                      En el dispositivo actual
                    </option> -->
                    <option v-for="s in formData.sectores" :key="s" :value="s">
                      Sector: {{ s }}
                    </option>
                  </TomSelect>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Impresion ante fallas  -->
        <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Impresión ante fallas
            </FormLabel>
          </div>

          <div class="flex-1 flex items-center gap-2">
            <FormCheck>
              <FormCheck.Input
                id="impresionAnteFallas"
                type="checkbox"
                v-model="formData.impresionAnteFallas"
              />
            </FormCheck>

            <div class="text-slate-500 text-sm">
              Si no puede imprimir en la ticketera correspondiente, imprimir en
              el dispositivoActual.
            </div>
          </div>
        </div>

        <!-- Categoria por defecto en producto manual -->
        <div class="col-span-12 lg:col-span-6">
          <div class="flex mt-5 items-start">
            <div class="flex-none w-50 text-right pr-5">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Categoría por defecto en detalle manual
              </FormLabel>
            </div>
            <TomSelect
              id="categoria"
              v-model="formData.categoriaDefectoManual"
              :class="{
                'border-danger': validate.categoriaDefectoManual.$error
              }"
              placeholder="Categoria por defecto"
              class="w-full md:w-1/8"
            >
              <option
                v-for="categoria in categoriasStore.categorias.filter(
                  data => data.activo
                )"
                :key="categoria.id"
                :value="categoria.id"
              >
                {{ categoria.nombre }}
              </option>
            </TomSelect>
          </div>
        </div>

        <!-- Imprimir comanda completa  -->
        <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Imprimir comanda completa
            </FormLabel>
          </div>

          <div class="flex-1 flex items-center gap-2">
            <FormCheck>
              <FormCheck.Input
                id="impresionAnteFallas"
                type="checkbox"
                v-model="formData.imprimirComandaCompleta"
              />
            </FormCheck>
          </div>
        </div>
        <!-- Impresión comanda completa -->
        <div
          v-if="formData.imprimirComandaCompleta"
          class="col-span-12 lg:col-span-6"
        >
          <div class="flex mt-5 items-start">
            <div class="flex-none w-50 text-right pr-5">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Imprimir comanda completa en
              </FormLabel>
            </div>

            <FormSelect
              id="impresionComandaCompleta"
              v-model="formData.impresionComandaCompleta"
              placeholder="Ticketera"
              class="w-full md:w-1/8"
            >
              <option
                v-for="ticketera in userStore.opcionesGeneralesTicketera
                  .ticketeras"
                :key="ticketera.nombre"
                :value="ticketera.idSector"
              >
                {{ ticketera.nombre }}
              </option>
            </FormSelect>
          </div>
        </div>

        <!-- Habilitar descuento -->
        <div class="flex mt-6">
          <div class="flex-1">
            <FormCheck>
              <FormCheck.Label class="ml-6">
                Habilitar descuentos
              </FormCheck.Label>
              <FormCheck.Input
                class="ml-5"
                id="habilitarDescuentos"
                type="checkbox"
                v-model="formData.habilitarDescuentos"
              />
            </FormCheck>
          </div>
        </div>
        <!-- Agregar descuento -->
        <div class="flex mt-6">
          <div class="flex-1">
            <FormCheck>
              <FormCheck.Label class="ml-6">
                Agregar descuentos
              </FormCheck.Label>
              <FormCheck.Input
                class="ml-5"
                id="agregarDescuentos"
                type="checkbox"
                v-model="mostrarAgregarDescuentos"
              />
            </FormCheck>
          </div>
        </div>
        <!-- Agregar descuento -->
        <!-- Habilitar descuentos -->
        <div class="flex mt-5" v-if="mostrarAgregarDescuentos">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="descuentos" class="w-40 sw:w-20">
              Agregar descuento
            </FormLabel>
          </div>

          <div class="flex-1">
            <!-- Nombre -->
            <FormInput
              type="text"
              id="descuentos"
              v-model.trim="formData.descuento.nombre"
              placeholder="Nombre del descuento (ej: 10% efectivo)"
              @keypress="
                e => {
                  if (e.key === ',') {
                    e.preventDefault();
                    agregarDescuento(e);
                  }
                }
              "
            />
            <div class="flex gap-3 mt-3 items-center">
              <div class="flex-1">
                <FormInput
                  type="number"
                  min="0"
                  step="0.01"
                  v-model.number="formData.descuento.valor"
                  placeholder="Valor (ej: 10)"
                />
              </div>

              <FormCheck class="flex items-center">
                <FormCheck.Input
                  id="descuentoActivo"
                  type="checkbox"
                  v-model="formData.descuento.activo"
                />
                <FormCheck.Label class="ml-2">Activo</FormCheck.Label>
              </FormCheck>
            </div>

            <Button
              class="my-3"
              variant="secondary"
              size="sm"
              type="button"
              @click="agregarDescuento"
            >
              Agregar
            </Button>
          </div>
        </div>

        <div class="col-span-12 lg:col-span-6 mt-3 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Descuentos
              </FormLabel>
            </div>

            <div class="w-full lg:w-1/4">
              <div
                v-if="(formData.descuentos?.length ?? 0) === 0"
                class="text-slate-500 text-sm mt-2"
              >
                No hay descuentos configurados.
              </div>

              <div v-else class="space-y-2">
                <div
                  v-for="(d, idx) in formData.descuentos"
                  :key="`${d.nombre}-${idx}`"
                  class="flex items-center gap-2 rounded-lg border border-slate-200/60 dark:border-darkmode-400 p-2"
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    class="px-2"
                    @click="toggleDescuentoActivo(idx)"
                  >
                    {{ d.activo ? "Activo" : "Inactivo" }}
                  </Button>

                  <div class="flex-1 min-w-0">
                    <div class="font-medium truncate">
                      {{ d.nombre }}
                    </div>
                    <div class="text-xs text-slate-500">
                      Valor: {{ d.valor }}
                    </div>
                  </div>

                  <Button
                    variant="danger"
                    size="sm"
                    type="button"
                    class="px-2"
                    @click="eliminarDescuento(idx)"
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Agrupar Items  -->
        <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Agrupar Items
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="agruparItems"
                type="checkbox"
                v-model="formData.agruparItems"
                value=""
              />
            </FormCheck>
          </div>
        </div>

        <!-- Factura con Fixed  -->
        <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Factura con Fixed
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="facturaFixed"
                type="checkbox"
                v-model="formData.facturaFixed"
                value=""
              />
            </FormCheck>
          </div>
        </div>

        <!-- Factura con Fixed defecto  -->
        <div class="flex mb-8" v-if="formData.facturaFixed">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Facturar con Fixed por defecto
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="fixedPorDefecto"
                type="checkbox"
                v-model="formData.fixedPorDefecto"
                value=""
              />
            </FormCheck>
          </div>
        </div>

        <!-- Integración con Fiserv  -->
        <!-- <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Integración con Fiserv
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="integracionHandy"
                type="checkbox"
                v-model="formData.integracionFiservHabilitado"
                value=""
              />
            </FormCheck>
          </div>
        </div> -->

        <!-- Integración con Handy  -->
        <!-- <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Integración con Handy (PosLink - Caja)</FormLabel
            >
          </div>
          <div class="flex-1">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="integracionHandy"
                type="checkbox"
                v-model="formData.integracionHandyHabilitado"
                value=""
              />
            </FormCheck>
          </div>
        </div> -->

        <!-- Integración con OCA  -->
        <!-- <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Integración con OCA
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="integracionOCA"
                type="checkbox"
                v-model="formData.integracionOCAHabilitado"
                value=""
              />
            </FormCheck>
          </div>
        </div> -->

        <!-- <div class="flex mb-8">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Integración con Getnet
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormCheck class="mt-2">
              <FormCheck.Input
                id="integracionGetnet"
                type="checkbox"
                v-model="formData.integracionGetnetHabilitado"
                value=""
              />
            </FormCheck>
          </div>
        </div> -->
        <div v-if="!cargando" class="flex mt-6">
          <div class="flex-1 text-left mt-3">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="() => emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <div class="flex-1 text-right">
            <div class="mt-3 text-right">
              <div
                class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap"
              >
                Para que los cambios sean aplicados en la aplicación movil debes
                ir a: Configuración → RECARGAR OPCIONES
              </div>
            </div>
            <Button
              variant="primary"
              class="mb-2 mr-1 mt-2"
              @click="handlerGuardar"
            >
              Guardar
            </Button>
          </div>
        </div>
        <div v-else class="flex my-6">
          <div class="mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </div>
    </form>
  </Preview>
</template>
